/* Seasonal decoration only. No product, form, cart or checkout state is changed. */
(() => {
  if (window.kirovaWinter) return;
  const caps = new Set();
  const candidates = new WeakSet();
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });
  const glyphs = new Map();
  let serial = 0;
  let frame;
  let observer;
  const selector = 'main h1, main h2, main h3, footer h2';
  const forbidden = 'button, label, input, textarea, select, script, style, svg, [hidden], .visually-hidden, [data-snow-decoration]';
  const ns = 'http://www.w3.org/2000/svg';
  const svgElement = (name, attributes = {}) => {
    const element = document.createElementNS(ns, name);
    for (const [key, value] of Object.entries(attributes)) element.setAttribute(key, value);
    return element;
  };
  const smoothPath = points => {
    if (!points.length) return '';
    let d = `M${points[0][0].toFixed(2)} ${points[0][1].toFixed(2)}`;
    for (let i = 1; i < points.length - 1; i++) {
      const x = (points[i][0] + points[i + 1][0]) / 2;
      const y = (points[i][1] + points[i + 1][1]) / 2;
      d += `Q${points[i][0].toFixed(2)} ${points[i][1].toFixed(2)} ${x.toFixed(2)} ${y.toFixed(2)}`;
    }
    const end = points[points.length - 1];
    return d + `L${end[0].toFixed(2)} ${end[1].toFixed(2)}`;
  };
  function glyphProfile(character, style) {
    const font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const key = font + character;
    if (glyphs.has(key)) return glyphs.get(key);
    const scale = 2;
    const size = parseFloat(style.fontSize);
    context.font = font;
    const measure = context.measureText(character);
    const ascent = measure.actualBoundingBoxAscent;
    const descent = Math.max(0, measure.actualBoundingBoxDescent);
    const left = measure.actualBoundingBoxLeft;
    const width = Math.ceil(left + measure.actualBoundingBoxRight);
    const height = Math.ceil(ascent + descent);
    if (!width || !height) return null;
    const pad = 3;
    canvas.width = Math.ceil((width + pad * 2) * scale);
    canvas.height = Math.ceil((height + pad * 2) * scale);
    context.scale(scale, scale);
    context.font = font;
    context.fillStyle = '#000';
    context.textBaseline = 'alphabetic';
    context.fillText(character, pad + left, pad + ascent);
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    const profile = [];
    for (let x = 0; x < width; x++) {
      const px = Math.round((x + pad) * scale);
      let top = null;
      for (let y = pad * scale; y < (pad + height) * scale; y++) {
        if (pixels[(y * canvas.width + px) * 4 + 3] > 96) { top = y / scale - pad; break; }
      }
      // Accumulation sits on the upper shoulders and follows the actual glyph outline.
      profile.push(top !== null && top <= height * .28 ? [x, top] : null);
    }
    const runs = [];
    let run = [];
    for (const point of profile) {
      if (point) run.push(point);
      else { if (run.length >= 3) runs.push(run); run = []; }
    }
    if (run.length >= 3) runs.push(run);
    const result = { runs, left, ascent, fontAscent: measure.fontBoundingBoxAscent || size * .9, width };
    if (glyphs.size > 220) glyphs.clear();
    glyphs.set(key, result);
    return result;
  }
  function drawSnow(host) {
    if (!host.isConnected || !context) return;
    host.querySelector(':scope > [data-snow-decoration]')?.remove();
    host.classList.remove('kirova-snowy-text');
    const style = getComputedStyle(host);
    const size = parseFloat(style.fontSize);
    const box = host.getBoundingClientRect();
    // Body copy, navigation, card labels and small headings keep their original letterforms.
    if (!/^H[1-3]$/.test(host.tagName) || size < 32 || !box.width || !box.height || style.visibility === 'hidden') return;
    const text = host.textContent.trim();
    if (!text || text.length > 180) return;
    const nodes = [];
    const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT, {
      acceptNode(node) { return node.parentElement.closest(forbidden) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT; }
    });
    while (walker.nextNode()) nodes.push(walker.currentNode);
    const id = `kirova-snow-${++serial}`;
    const overlay = svgElement('svg', { class: 'kirova-text-snow', 'data-snow-decoration': '', 'aria-hidden': 'true', focusable: 'false', width: box.width, height: box.height, viewBox: `0 0 ${box.width} ${box.height}` });
    const defs = svgElement('defs');
    const gradient = svgElement('linearGradient', { id, x1: '0', y1: '0', x2: '0', y2: '1' });
    for (const [offset, color] of [['0','#fff'],['.68','#fff'],['1','#d8eaf2']]) gradient.append(svgElement('stop', { offset, 'stop-color': color }));
    defs.append(gradient);
    overlay.append(defs);
    let counter = 0;
    let total = 0;
    for (const node of nodes) {
      const fontStyle = getComputedStyle(node.parentElement);
      const fontSize = parseFloat(fontStyle.fontSize);
      if (fontSize < 32) continue;
      let offset = 0;
      for (const character of node.textContent) {
        const start = offset;
        offset += character.length;
        if (!/[\p{L}\p{N}]/u.test(character)) continue;
        counter++;
        if (counter > 110) break;
        const range = document.createRange();
        range.setStart(node, start);
        range.setEnd(node, offset);
        const char = range.getBoundingClientRect();
        if (!char.width) continue;
        const profile = glyphProfile(character, fontStyle);
        if (!profile) continue;
        const originX = char.left - box.left - profile.left;
        const originY = char.top - box.top + profile.fontAscent - profile.ascent;
        const depth = Math.min(9, Math.max(3.3, fontSize * .075));
        for (const run of profile.runs) {
          const seed = character.codePointAt(0) + counter * 17;
          const top = [];
          const bottom = [];
          const span = run[run.length - 1][0] - run[0][0];
          const drip = fontSize >= 42 && counter % 3 === 1 && span > 10;
          const dripX = run[0][0] + span * (.64 + (seed % 3) * .09);
          const dripHalf = Math.max(1.2, fontSize * .018);
          const dripLength = Math.min(12, fontSize * (.085 + (seed % 4) * .015));
          for (let i = 0; i < run.length; i++) {
            const [x, y] = run[i];
            const edge = Math.sin(Math.PI * (i + .6) / (run.length + .2));
            const fluff = .78 + .15 * Math.sin(x * .48 + seed) + .08 * Math.cos(x * .83 + seed);
            top.push([originX + x, originY + y - depth * fluff * Math.pow(Math.max(.05, edge), .3)]);
            const distance = Math.abs(x - dripX) / dripHalf;
            const icicle = drip && distance < 1 ? dripLength * Math.pow(1 - distance, .48) : 0;
            bottom.push([originX + x, originY + y + depth * .15 + icicle]);
          }
          const bottomPath = smoothPath(bottom.reverse()).replace(/^M/, 'L');
          const path = svgElement('path', { d: smoothPath(top) + bottomPath + 'Z', fill: `url(#${id})`, class: 'kirova-text-snow__contour' });
          overlay.append(path);
          total++;
        }
      }
    }
    if (total) {
      host.classList.add('kirova-snowy-text');
      host.append(overlay);
    }
  }
  const schedule = () => {
    clearTimeout(frame);
    frame = setTimeout(() => { for (const host of caps) host.isConnected ? drawSnow(host) : caps.delete(host); }, 140);
  };
  function scan(root = document) {
    if (document.body.dataset.kirovaTextSnow !== 'true') return;
    const list = [...root.querySelectorAll(selector)];
    if (root.matches && root.matches(selector)) list.push(root);
    for (const element of list) {
      if (candidates.has(element) || element.closest(forbidden)) continue;
      candidates.add(element);
      caps.add(element);
      if (observer) observer.observe(element); else drawSnow(element);
    }
  }
  function snowfall() {
    const layer = document.querySelector('[data-winter-overlay]');
    if (!layer || document.body.dataset.kirovaSnowfall !== 'true') return;
    const mobile = matchMedia('(max-width: 749px)').matches;
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < (mobile ? 30 : 60); i++) {
      const flake = document.createElement('span');
      const detailed = i % 3 === 0;
      flake.className = `kirova-falling-snow${detailed ? ' kirova-falling-snow--crystal' : ''}`;
      flake.style.cssText = `--snow-x:${(i * 37.73) % 100}vw;--snow-size:${detailed ? 11 + i % 12 : 3 + i % 4}px;--snow-duration:${12 + i % 17}s;--snow-delay:-${(i * 3.41) % 27}s;--snow-drift:${(i % 2 ? -1 : 1) * (12 + i % 25)}px;--snow-opacity:${.5 + (i % 5) * .1}`;
      if (detailed) flake.innerHTML = '<svg viewBox="0 0 32 32" focusable="false"><use href="#kirova-winter-flake"></use></svg>';
      fragment.append(flake);
    }
    layer.replaceChildren(fragment);
    layer.dataset.ready = 'true';
  }
  function boot() {
    if (document.body.dataset.kirovaHoliday !== 'true') return;
    if ('IntersectionObserver' in window) observer = new IntersectionObserver(entries => {
      for (const entry of entries) if (entry.isIntersecting) drawSnow(entry.target);
    }, { rootMargin: '120px' });
    snowfall();
    scan();
    if (document.fonts) document.fonts.ready.then(() => { glyphs.clear(); schedule(); });
    addEventListener('resize', schedule, { passive: true });
    document.addEventListener('shopify:section:load', event => { scan(event.target); schedule(); });
    document.addEventListener('shopify:section:unload', () => { for (const host of caps) if (!host.isConnected) caps.delete(host); });
    document.addEventListener('visibilitychange', () => document.body.classList.toggle('kirova-snow-paused', document.hidden));
    const mutation = new MutationObserver(records => {
      for (const record of records) for (const node of record.addedNodes) {
        if (node.nodeType !== 1 || node.matches('[data-snow-decoration], .kirova-text-snow__pile') || node.closest('[data-winter-overlay]')) continue;
        scan(node);
      }
    });
    const main = document.querySelector('main');
    if (main) mutation.observe(main, { childList: true, subtree: true });
  }
  window.kirovaWinter = { redraw: schedule };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot, { once: true }) : boot();
})();
